import { Popover, Table, Theme } from '@radix-ui/themes'
import CantileverButton from 'components/CantileverButton'
import {
  convertToUnit,
  generateToggleFieldsFromFormFields,
  useGeckoForm,
} from 'components/form/formHelpers'
import {
  MeasurementApplication,
  PermissionsCheck,
} from 'components/form/formTypes'
import { CopyIcon, Ellipsis, TrashIcon } from 'lucide-react'
import React, { forwardRef, useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import styled from 'styled-components'

import { ToggleFormField } from './formFields/ToggleFormField'
import { FormComponentProps } from './GeckoForm'
import { TableFormHeader } from './TableFormHeader'
import {
  StyledActionCell,
  StyledCell,
  StyledRow,
  StyledTableRoot,
} from './TableFormStyles'

interface GeckoTableFormProps extends FormComponentProps {
  inEditMode: boolean
  inResolveMode?: boolean
  measurementApplication?: MeasurementApplication
  permissionsCheck?: PermissionsCheck
  handleDeleteByIndex?: (index: number) => void
  handleDeleteByID?: (featureToRemove: string) => void
  handleDuplicateByID?: (featureToDuplicate: string) => void
}

const StyledPopoverContent = styled(Popover.Content)`
  border-radius: 0.25rem;
  z-index: 1000;
  background-color: ${({ theme }) => theme.forecast.modalBackground};
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 2rem;
`

const ActionMenuItem = styled.div<{ $warning?: boolean }>`
  background-color: ${({ theme, $warning }) =>
    $warning
      ? theme.forecast.redGlassBackground
      : theme.forecast.modalBackground};
  color: ${({ theme }) => theme.forecast.text1};
  padding: 0.25rem;
  padding-left: 0.5rem;
  height: 100%;
  cursor: pointer;
  display: flex;
  gap: 1rem;
  width: 13rem;
  align-items: center;
  &:hover {
    background-color: ${({ theme, $warning }) =>
      $warning
        ? theme.forecast.redGlassBackground
        : theme.forecast.hoverGhostButton};
  }
`

export const GeckoTableForm = forwardRef<HTMLFormElement, GeckoTableFormProps>(
  (
    {
      inEditMode,
      inResolveMode,
      measurementApplication,
      permissionsCheck,
      formFieldsByUniqueID,
      onSubmit,
      handleDeleteByIndex,
      handleDeleteByID,
      handleDuplicateByID,
      propForm,
    },
    ref
  ) => {
    const [showDelete, setShowDelete] = useState(false)

    const useForm = useGeckoForm(formFieldsByUniqueID)
    const form = propForm ?? useForm
    const tableColumnHeaders = useMemo(
      () =>
        formFieldsByUniqueID.map((formFieldByUniqueID) =>
          formFieldByUniqueID.formFields.map((formField) => {
            const { label = '', unit_magnitude } = formField
            const displayUnit = measurementApplication
              ? convertToUnit(
                  measurementApplication.measurementSystem,
                  unit_magnitude
                )
              : null
            const header = `${label}${displayUnit ? ` (${displayUnit.name})` : ''}`
            return { label, header }
          })
        )[0],
      [formFieldsByUniqueID, measurementApplication]
    )

    return (
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} ref={ref}>
          <StyledTableRoot>
            <TableFormHeader
              inEditMode={inEditMode}
              tableColumnHeaders={tableColumnHeaders}
            />
            <Table.Body>
              {formFieldsByUniqueID.map((formFieldByUniqueID, index) => (
                <StyledRow key={formFieldByUniqueID.uniqueID}>
                  {generateToggleFieldsFromFormFields(
                    formFieldByUniqueID.formFields,
                    formFieldByUniqueID.uniqueID,
                    measurementApplication,
                    permissionsCheck
                  ).map((formField) => (
                    <StyledCell key={formField.formItemKey}>
                      <Theme asChild hasBackground={false}>
                        <ToggleFormField
                          key={formField.formItemKey}
                          formItemKey={formField.formItemKey}
                          formField={formField.formField}
                          fieldValue={formField.formFieldValue}
                          fieldUnits={formField.formFieldUnits}
                          canEditField={formField.canEditField}
                          canReadField={formField.canReadField}
                          units={formField.units}
                          formControl={form.control}
                          formErrors={form.formState.errors}
                          formTrigger={form.trigger}
                          formSetValue={form.setValue}
                          inEditMode={inEditMode}
                          inResolveMode={inResolveMode}
                          showLabel={false}
                        />
                      </Theme>
                    </StyledCell>
                  ))}
                  {inEditMode && handleDeleteByIndex && (
                    <StyledActionCell>
                      <CantileverButton
                        danger
                        onClick={() => handleDeleteByIndex(index)}>
                        Delete
                      </CantileverButton>
                    </StyledActionCell>
                  )}
                  {inEditMode && handleDeleteByID && (
                    <Popover.Root
                      onOpenChange={(open) => !open && setShowDelete(false)}>
                      <Popover.Trigger>
                        <StyledActionCell>
                          <CantileverButton ghost>
                            <Ellipsis />
                          </CantileverButton>
                        </StyledActionCell>
                      </Popover.Trigger>
                      <StyledPopoverContent side='right'>
                        {showDelete ? (
                          <ActionMenuItem
                            $warning
                            onClick={() => {
                              handleDeleteByID(formFieldByUniqueID.uniqueID)
                              setShowDelete(false)
                            }}>
                            <TrashIcon size={15} />
                            <span>Click Again To Confirm Deletion</span>
                          </ActionMenuItem>
                        ) : (
                          <ActionMenuItem onClick={() => setShowDelete(true)}>
                            <TrashIcon size={15} />
                            <span>Delete Row</span>
                          </ActionMenuItem>
                        )}
                        {handleDuplicateByID && (
                          <ActionMenuItem
                            onClick={() => {
                              handleDuplicateByID(formFieldByUniqueID.uniqueID)
                            }}>
                            <CopyIcon size={15} />
                            <span>Copy Row</span>
                          </ActionMenuItem>
                        )}
                      </StyledPopoverContent>
                    </Popover.Root>
                  )}
                </StyledRow>
              ))}
            </Table.Body>
          </StyledTableRoot>
        </form>
      </FormProvider>
    )
  }
)

GeckoTableForm.displayName = 'GeckoTableForm'
